export type VesselContactEntry = {
  value: string
  primary: boolean
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^[0-9+()\-\s]{3,32}$/

const normalizeEmailKey = (value: string) => value.trim().toLowerCase()
const normalizePhoneKey = (value: string) => value.trim().replace(/\s+/g, '')

const ensurePrimary = (entries: VesselContactEntry[]): VesselContactEntry[] => {
  const nonEmptyIndexes = entries
    .map((entry, index) => ({entry, index}))
    .filter(({entry}) => entry.value.trim() !== '')
    .map(({index}) => index)

  if (nonEmptyIndexes.length === 0) {
    return entries.map((entry, index) => ({...entry, primary: index === 0}))
  }

  const currentPrimary = nonEmptyIndexes.find((index) => entries[index]?.primary)
  const primaryIndex = currentPrimary ?? nonEmptyIndexes[0]

  return entries.map((entry, index) => ({
    ...entry,
    primary: entry.value.trim() !== '' && index === primaryIndex,
  }))
}

export const buildContactEntries = (values?: string[], singleValue?: string | null): VesselContactEntry[] => {
  const orderedValues =
    values && values.length > 0
      ? values
      : singleValue && singleValue.trim()
        ? [singleValue.trim()]
        : ['']

  return ensurePrimary(
    orderedValues.map((value, index) => ({
      value,
      primary: index === 0,
    }))
  )
}

export const updateContactValue = (
  entries: VesselContactEntry[],
  index: number,
  value: string
): VesselContactEntry[] => ensurePrimary(entries.map((entry, current) => (
  current === index ? {...entry, value} : entry
)))

export const addContactEntry = (entries: VesselContactEntry[]): VesselContactEntry[] =>
  [...ensurePrimary(entries), {value: '', primary: false}]

export const removeContactEntry = (entries: VesselContactEntry[], index: number): VesselContactEntry[] => {
  const next = entries.filter((_, current) => current !== index)
  return ensurePrimary(next.length > 0 ? next : [{value: '', primary: true}])
}

export const setPrimaryContact = (entries: VesselContactEntry[], index: number): VesselContactEntry[] =>
  ensurePrimary(entries.map((entry, current) => ({
    ...entry,
    primary: current === index && entry.value.trim() !== '',
  })))

export const serializeContactEntries = (entries: VesselContactEntry[]): string[] => {
  const normalized = ensurePrimary(entries)
    .filter((entry) => entry.value.trim() !== '')

  const primary = normalized.filter((entry) => entry.primary)
  const secondary = normalized.filter((entry) => !entry.primary)
  return [...primary, ...secondary].map((entry) => entry.value.trim())
}

export const getPrimaryContactValue = (entries: VesselContactEntry[]): string => {
  const serialized = serializeContactEntries(entries)
  return serialized[0] ?? ''
}

export const validateContactEntries = (
  entries: VesselContactEntry[],
  kind: 'phone' | 'email'
): string | null => {
  const serialized = serializeContactEntries(entries)
  if (serialized.length === 0) {
    return null
  }

  const pattern = kind === 'phone' ? PHONE_RE : EMAIL_RE
  const seen = new Set<string>()

  for (const value of serialized) {
    const key = kind === 'phone' ? normalizePhoneKey(value) : normalizeEmailKey(value)
    const normalizedValue = kind === 'phone' ? value.trim() : normalizeEmailKey(value)

    if (!pattern.test(normalizedValue)) {
      return kind === 'phone' ? 'Enter valid phone numbers only.' : 'Enter valid email addresses only.'
    }

    if (seen.has(key)) {
      return kind === 'phone'
        ? 'Duplicate phone numbers are not allowed.'
        : 'Duplicate email addresses are not allowed.'
    }

    seen.add(key)
  }

  return null
}
